import * as signalNames from './signalNames';
import { DATES, SOFTWARE, TEXT_LAYER_TEXT_GROUP, ANOMALY } from './metadata';
import {
  COLOR_DATA_VIS_1,
  COLOR_DATA_VIS_2,
  COLOR_DATA_VIS_3,
  COLOR_DATA_VIS_4,
  COLOR_DATA_VIS_5,
  COLOR_DATA_VIS_6,
  COLOR_DATA_VIS_7,
  COLOR_DATA_VIS_8,
  COLOR_DATA_VIS_9,
  COLOR_DATA_VIS_10,
  COLOR_DATA_VIS_11,
  COLOR_DATA_VIS_12,
  COLOR_DATA_VIS_13,
} from './colors';
import { downloadDocument } from '../utils/download';

export const PREFERRED_SIGNAL_ORDER = [
  signalNames.XRAY,
  signalNames.FONTS,
  signalNames.MASKING,
  signalNames.SOFTWARE_BLOCKLIST,
  signalNames.EDITED_TEXT,
  signalNames.TEXT_COMPRESSION,
  signalNames.TEXT_LAYER_TEXT,
  signalNames.PDF_ALIGNMENT,
  signalNames.FINGERPRINT,
  signalNames.CUSTOM_RULES,
  signalNames.BLOCKLISTS,
  signalNames.TEMPLATES,
  signalNames.REJECTED_CUSTOMER,
  signalNames.ADOBE_FONTS,
  signalNames.EDITED_WHILE_SCANNED,
  signalNames.OVERLAID_TEXT,
  signalNames.ANNOTATION_DATES,
  signalNames.MALFORMED_DATE,
  signalNames.DATE_METADATA,
  signalNames.TOUCHUP_TEXT,
  signalNames.QPDF,
  signalNames.EXIF_DATA_MODIFIED,
  signalNames.HIGH_USER_ACCESS,
  signalNames.ANNOTATIONS,
  signalNames.FLATTENED_PDF,
  signalNames.SEEN_BEFORE,
  signalNames.BRUSH_DETECTION,
  signalNames.COPYCAT_IMAGE,
  signalNames.COPYCAT_TEXT,
  signalNames.FONT_ANOMALY,
  signalNames.METADATA_ANOMALY,
  signalNames.NAME_FORMAT_ANOMALY,
  signalNames.ADDRESS_FORMAT_ANOMALY,
  signalNames.DATE_FORMAT_ANOMALY,
  signalNames.REPEAT_APPLICATION,
  signalNames.REPEAT_APPLICATION_DIFFERENT_ADDRESS,
  signalNames.REPEAT_APPLICATION_DIFFERENT_NAME,
  signalNames.REPEAT_APPLICATION_PREVIOUS_FRAUD,
  signalNames.REPEAT_APPLICATION_PREVIOUS_REJECTION,
];

const sumIssuesAcrossPages = (pages, getIssuesPerPage) =>
  pages.reduce((sum, page) => sum + getIssuesPerPage(page), 0);

const sumSuspiciousRegions = (pages) => {
  return sumIssuesAcrossPages(pages, ({ instances }) => instances.length);
};

const getPagesText = (numPages) =>
  numPages > 1 ? ` across ${numPages} page${numPages > 1 ? 's' : ''}` : '';

export default {
  [signalNames.FONTS]: {
    icon: 'font',
    title: 'Font issues',
    getDescription: ({ pages }) => {
      const issues = sumIssuesAcrossPages(
        pages,
        ({ inconsistent_words: words }) => words.length,
      );
      return `${issues} inconsistenc${issues === 1 ? 'y' : 'ies'}${getPagesText(
        pages.length,
      )}`;
    },
    helpArticle: '3496550-fonts',
    color: COLOR_DATA_VIS_1,
  },
  [signalNames.MASKING]: {
    icon: 'draw-polygon',
    title: 'Masking',
    getDescription: ({ pages }) => {
      const numRegions = sumSuspiciousRegions(pages);
      return `${numRegions} masked region${
        numRegions > 1 ? 's' : ''
      }${getPagesText(pages.length)}`;
    },
    helpArticle: '3505122-masking',
    color: COLOR_DATA_VIS_6,
  },
  [signalNames.TEXT_COMPRESSION]: {
    icon: 'text-width',
    title: 'Text compression',
    getDescription: ({ pages }) => {
      const numRegions = sumSuspiciousRegions(pages);
      return `${numRegions} suspicious region${
        numRegions > 1 ? 's' : ''
      }${getPagesText(pages.length)}`;
    },
    helpArticle: '3672332-text-compression',
    color: COLOR_DATA_VIS_2,
  },
  [signalNames.BRUSH_DETECTION]: {
    icon: 'paint-roller',
    title: 'Brush detection',
    getDescription: ({ pages }) => {
      const numRegions = sumSuspiciousRegions(pages);
      return `${numRegions} suspicious region${
        numRegions > 1 ? 's' : ''
      }${getPagesText(pages.length)}`;
    },
    helpArticle: '5291307-brush-detection',
    color: COLOR_DATA_VIS_8,
  },
  [signalNames.PDF_ALIGNMENT]: {
    icon: 'align-left',
    title: 'PDF Alignment',
    getDescription: ({ pages }) => {
      let description = '';
      if (pages.length === 1) {
        description = pages[0].description;
      } else if (pages.length > 1) {
        const numRegions = sumSuspiciousRegions(pages);
        description = `${numRegions} suspicious region${
          numRegions > 1 ? 's' : ''
        }${getPagesText(pages.length)}`;
      }
      return description;
    },
    helpArticle: '5402701-pdf-alignment',
    color: COLOR_DATA_VIS_10,
  },
  [signalNames.OVERLAID_TEXT]: {
    icon: 'layer-group',
    title: 'Overlaid text',
    helpArticle: '3505126-overlaid-text',
    color: COLOR_DATA_VIS_3,
  },
  [signalNames.EDITED_TEXT]: {
    icon: 'pen-to-square',
    title: 'Edited text',
    getDescription: ({ pages }) => {
      const numRegions = sumSuspiciousRegions(pages);
      return `${numRegions} suspicious region${
        numRegions > 1 ? 's' : ''
      }${getPagesText(pages.length)}`;
    },
    helpArticle: '3557476-edited-text',
    color: COLOR_DATA_VIS_9,
  },
  [signalNames.FINGERPRINT]: {
    icon: 'fingerprint',
    title: 'Fingerprint',
    helpArticle: '4377726-fingerprinting',
    getDescription: ({ institution }) => {
      return `${institution.name} fingerprint mismatch`;
    },
    color: COLOR_DATA_VIS_5,
  },
  [signalNames.DATE_METADATA]: {
    icon: 'calendar',
    title: 'Dates',
    relatedMetadata: DATES,
    helpArticle: '3505132-metadata',
  },
  [signalNames.QPDF]: {
    icon: 'list',
    title: 'Edited with QPDF',
  },
  [signalNames.EXIF_DATA_MODIFIED]: {
    icon: 'list',
    title: 'Exif issue',
    getDescription: () => 'Metadata edited with exif tool',
  },
  [signalNames.EDITED_WHILE_SCANNED]: {
    icon: 'marker',
    title: 'Edit after scan',
    getDescription: () => 'Document edited after scanning',
    helpArticle: '3505132-metadata',
  },
  [signalNames.HIGH_USER_ACCESS]: {
    icon: 'shield-halved',
    title: 'Editing allowed',
  },
  [signalNames.ADOBE_FONTS]: {
    icon: 'text',
    title: 'Adobe fonts',
    getDescription: () => 'Text edited using Adobe Fonts',
  },
  [signalNames.ANNOTATION_DATES]: {
    icon: 'calendar',
    title: 'Annotation dates',
    helpArticle: '3505132-metadata',
  },
  [signalNames.ANNOTATIONS]: {
    icon: 'message',
    title: 'Annotations',
    helpArticle: '9088193-annotations',
  },
  [signalNames.MALFORMED_DATE]: {
    icon: 'calendar',
    title: 'Malformed date',
    helpArticle: '3505132-metadata',
  },
  [signalNames.TEXT_LAYER_TEXT]: {
    icon: 'text',
    title: 'Photoshop',
    helpArticle: '3505132-metadata',
    relatedMetadata: TEXT_LAYER_TEXT_GROUP,
  },
  [signalNames.FLATTENED_PDF]: {
    icon: 'weight-hanging',
    title: 'Flattened PDF',
    helpArticle: '4680342-flattened-pdf',
  },
  [signalNames.IS_NOT_BALANCED]: {
    title: 'Transactions do not balance',
    helpArticle: '9317949-transactions-do-not-balance',
  },
  [signalNames.TOUCHUP_TEXT]: {
    icon: 'pen-to-square',
    title: 'Touch-up text',
  },
  [signalNames.CUSTOM_RULES]: {
    icon: 'bolt',
    title: 'Custom rules',
    getDescription: (signal) => {
      const triggeredRules = Object.keys(signal).filter((key) => !isNaN(key));
      const num = triggeredRules.length;
      return triggeredRules.length
        ? `${num} custom rule${num > 1 ? 's' : ''} triggered`
        : '';
    },
  },
  [signalNames.SOFTWARE_BLOCKLIST]: {
    icon: 'list',
    title: 'Software',
    getDescription: ({ description }) => description || 'No software issues',
    relatedMetadata: SOFTWARE,
    helpArticle: '3505132-metadata',
  },
  [signalNames.SEEN_BEFORE]: {
    icon: 'clock-rotate-left',
    title: 'Seen before',
    getDescription: ({ documents }) =>
      `Document has been submitted before${
        documents && documents.length > 1 ? ` ${documents.length} times` : ''
      }`,
    helpArticle: '3505127-seen-before',
  },
  [signalNames.XRAY]: {
    icon: 'window-restore',
    title: 'X-ray',
    ctaHandler: ({ recovered_version }) =>
      downloadDocument(recovered_version.url, 'recovered-version.pdf'),
    getCtaText: () => 'Download',
    getDescription: () => 'Previous version has been recovered',
    helpArticle: '3505129-x-ray-previous-version-recovered',
  },
  [signalNames.BLOCKLISTS]: {
    icon: 'user-xmark',
    title: 'Blocklists',
    getDescription: ({ pages }) => {
      return `Blocklisted property '${pages[0].description}'${
        pages.length > 1 ? ` and ${pages.length - 1} more` : ''
      } found`;
    },
    helpArticle: '3650412-blacklist-database',
  },
  [signalNames.REJECTED_CUSTOMER]: {
    icon: 'person-circle-exclamation',
    title: 'Rejected customer',
    getDescription: () =>
      `Customer with this address has been previously rejected`,
    helpArticle: '3959143-parsing',
  },
  [signalNames.TEMPLATES]: {
    icon: 'images',
    title: 'Templates',
    getDescription: () =>
      'This document matches a template from a document store',
    getCtaText: () => 'View',
    helpArticle: '3650410-template-database',
  },
  [signalNames.NAME]: {
    icon: 'user',
    title: 'name',
    getTitle: ({ failed }) =>
      failed ? 'Name verification failed' : 'Name successfully verified',
  },
  [signalNames.ADDRESS]: {
    icon: 'house',
    title: 'address',
    getTitle: ({ failed }) =>
      failed ? 'Address verification failed' : 'Address successfully verified',
  },
  [signalNames.COMPANY]: {
    icon: 'building',
    title: 'company',
    getTitle: ({ failed }) =>
      failed ? 'Company verification failed' : 'Company successfully verified',
  },
  [signalNames.ID_NUMBER]: {
    icon: 'hashtag',
    title: 'ID number',
    getTitle: ({ failed }) =>
      failed
        ? 'ID number verification failed'
        : 'ID number successfully verified',
  },
  [signalNames.STRINGS]: {
    icon: 'text',
    title: 'Strings',
    getTitle: (signal) => {
      if (signal.key) {
        return signal.failed
          ? `${signal.key} verification failed`
          : `${signal.key} successfully verified`;
      } else {
        return signal.failed
          ? 'String verification failed'
          : 'String successfully verified';
      }
    },
  },
  [signalNames.COPYCAT_IMAGE]: {
    icon: 'camera',
    title: 'Copycat Image',
    getDescription: () => 'Image seen before under a different identity',
    helpArticle: '5908765-copycat-image',
  },
  [signalNames.COPYCAT_TEXT]: {
    icon: 'keyboard',
    title: 'Copycat Text',
    getDescription: () =>
      'Document contents seen before under a different identity',
    helpArticle: '6015469-copycat-text',
  },
  [signalNames.FONT_ANOMALY]: {
    icon: 'link-slash',
    title: 'Font Anomaly',
    relatedMetadata: ANOMALY,
    color: COLOR_DATA_VIS_11,
    helpArticle: '6084222-anomaly-detection',
  },
  [signalNames.METADATA_ANOMALY]: {
    icon: 'triangle-exclamation',
    title: 'Metadata Anomaly',
    relatedMetadata: ANOMALY,
    helpArticle: '6084222-anomaly-detection',
  },
  [signalNames.NAME_FORMAT_ANOMALY]: {
    icon: 'user',
    title: 'Name Format Anomaly',
    getDescription: () => 'Name case does not match similar documents',
    color: COLOR_DATA_VIS_4,
    helpArticle: '6084222-anomaly-detection',
  },
  [signalNames.ADDRESS_FORMAT_ANOMALY]: {
    icon: 'building',
    title: 'Address Format Anomaly',
    getDescription: () => 'Address case does not match similar documents',
    color: COLOR_DATA_VIS_7,
    helpArticle: '6084222-anomaly-detection',
  },
  [signalNames.DATE_FORMAT_ANOMALY]: {
    icon: 'calendar',
    title: 'Date Format Anomaly',
    getDescription: () => 'Date format does not match similar documents',
    color: COLOR_DATA_VIS_12,
    helpArticle: '6084222-anomaly-detection',
  },
  [signalNames.AMOUNT_FORMAT_ANOMALY]: {
    icon: 'apostrophe',
    title: 'Amount Format Anomaly',
    getDescription: () => 'Amount format does not match similar documents',
    color: COLOR_DATA_VIS_13,
    helpArticle: '6084222-anomaly-detection',
  },
  [signalNames.REPEAT_APPLICATION]: {
    icon: 'clock-rotate-left',
    title: 'Repeat Application',
    getDescription: () =>
      'User device previously associated with another application',
  },
  [signalNames.REPEAT_APPLICATION_DIFFERENT_ADDRESS]: {
    icon: 'clock-rotate-left',
    title: 'Repeat Application with Different Address',
    getDescription: () =>
      'User device previously associated with a different address',
  },
  [signalNames.REPEAT_APPLICATION_DIFFERENT_NAME]: {
    icon: 'clock-rotate-left',
    title: 'Repeat Application with Different Name',
    getDescription: () =>
      'User device previously associated with a different name',
  },
  [signalNames.REPEAT_APPLICATION_PREVIOUS_FRAUD]: {
    icon: 'clock-rotate-left',
    title: 'Repeat Application with Previous Fraud',
    getDescription: () =>
      'User device previously associated with fraudulent documents',
  },
  [signalNames.REPEAT_APPLICATION_PREVIOUS_REJECTION]: {
    icon: 'clock-rotate-left',
    title: 'Repeat Application with Previous Rejection',
    getDescription: () =>
      'User device previously associated with a rejected Customer',
  },
};
